/**
 * Integer money helpers. All amounts are integer MINOR units of an ISO 4217 currency
 * (sen for MYR, cents for USD/EUR, yen for JPY, fils for KWD — 3 decimals).
 * Never use floats for stored or computed money; these helpers throw on non-integers.
 * Salons operate worldwide: never assume 2 decimals — always go through currencyExponent().
 */
export type CurrencyCode = string; // ISO 4217 alpha-3, upper case
export type Minor = number;

/**
 * ISO 4217 minor-unit exponents that differ from 2, plus common 2-decimal currencies.
 * Unknown-but-valid codes fall back to Intl (which follows CLDR/ISO for most currencies).
 */
const EXPONENT: Record<string, number> = {
  // 0 decimals
  JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0, PYG: 0, UGX: 0, XAF: 0, XOF: 0, XPF: 0, KMF: 0, RWF: 0, GNF: 0, DJF: 0, VUV: 0, BIF: 0,
  // 3 decimals
  KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3, LYD: 3, IQD: 3,
  // 2 decimals (explicit so behaviour never depends on the JS engine)
  MYR: 2, USD: 2, EUR: 2, GBP: 2, SGD: 2, AUD: 2, NZD: 2, CAD: 2, HKD: 2, CNY: 2, TWD: 2, THB: 2, PHP: 2, IDR: 2, INR: 2,
  AED: 2, SAR: 2, QAR: 2, CHF: 2, SEK: 2, NOK: 2, DKK: 2, PLN: 2, CZK: 2, HUF: 2, BRL: 2, MXN: 2, ZAR: 2, TRY: 2, BND: 2,
};

/** Legacy compact symbols used when no display locale is given (POS receipts, tests). */
const SYMBOL: Record<string, string> = { MYR: "RM", USD: "US$", SGD: "S$", JPY: "¥", EUR: "€", GBP: "£", KWD: "KD ", AUD: "A$", THB: "฿", IDR: "Rp", PHP: "₱", INR: "₹", HKD: "HK$", CNY: "CN¥", KRW: "₩", VND: "₫", AED: "AED " };

const CODE_RE = /^[A-Z]{3}$/;

export function isCurrencyCode(code: string): boolean {
  if (!CODE_RE.test(code)) return false;
  if (code in EXPONENT) return true;
  try { new Intl.NumberFormat("en", { style: "currency", currency: code }); return true; } catch { return false; }
}

export function currencyExponent(currency: CurrencyCode): number {
  const e = EXPONENT[currency];
  if (e !== undefined) return e;
  if (!CODE_RE.test(currency)) throw new Error("Unsupported currency: " + currency);
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    throw new Error("Unsupported currency: " + currency);
  }
}

export function assertMinor(value: unknown, label = "amount"): asserts value is Minor {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new TypeError(label + " must be a safe integer in minor units, got " + String(value));
  }
}

/** Parse a decimal string like "1,234.50" or "-12.3" into minor units without float math. */
export function toMinor(input: string | number, currency: CurrencyCode = "MYR"): Minor {
  const exp = currencyExponent(currency);
  let s = typeof input === "number" ? input.toFixed(exp) : input.trim();
  s = s.replace(/,/g, "").replace(/^(RM|US\$|S\$|¥|€|£|KD|[A-Z]{3})\s*/i, "");
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) throw new TypeError("Invalid money string: " + String(input));
  const [, neg, whole, fracRaw = ""] = m;
  if (fracRaw.length > exp) throw new RangeError("Too many decimal places for " + currency + ": " + String(input));
  const frac = fracRaw.padEnd(exp, "0");
  const minor = Number(whole + frac);
  if (!Number.isSafeInteger(minor)) throw new RangeError("Amount out of safe range");
  return neg && minor !== 0 ? -minor : minor;
}

/** Exact decimal string for minor units, e.g. (123456, "KWD") -> "123.456", (500, "JPY") -> "500". */
export function minorToDecimal(minor: Minor, currency: CurrencyCode): string {
  assertMinor(minor);
  const exp = currencyExponent(currency);
  const neg = minor < 0;
  const abs = Math.abs(minor);
  const base = 10 ** exp;
  const whole = Math.floor(abs / base).toString();
  const frac = exp > 0 ? "." + (abs % base).toString().padStart(exp, "0") : "";
  return (neg ? "-" : "") + whole + frac;
}

const nfCache = new Map<string, Intl.NumberFormat>();

/**
 * Format minor units for display.
 *  - with opts.locale (BCP 47, e.g. "en-MY", "ja-JP", "ar-KW"): Intl.NumberFormat currency style,
 *    fed an exact decimal string (no float rounding);
 *  - without a locale: compact legacy style, e.g. 248000 -> "RM2,480.00".
 */
export function formatMoney(
  minor: Minor,
  currency: CurrencyCode = "MYR",
  opts: { symbol?: boolean; trimZeroCents?: boolean; locale?: string } = {},
): string {
  assertMinor(minor);
  const exp = currencyExponent(currency);
  if (opts.locale) {
    const trim = !!opts.trimZeroCents && minor % 10 ** exp === 0;
    const key = opts.locale + "|" + currency + "|" + (opts.symbol === false ? "n" : "s") + (trim ? "t" : "");
    let nf = nfCache.get(key);
    if (!nf) {
      nf = new Intl.NumberFormat(opts.locale, opts.symbol === false
        ? { minimumFractionDigits: trim ? 0 : exp, maximumFractionDigits: exp }
        : { style: "currency", currency, minimumFractionDigits: trim ? 0 : exp, maximumFractionDigits: exp });
      nfCache.set(key, nf);
    }
    // Intl accepts decimal strings and formats them exactly.
    return nf.format(minorToDecimal(minor, currency) as unknown as number);
  }
  const neg = minor < 0;
  const abs = Math.abs(minor);
  const base = 10 ** exp;
  const whole = Math.floor(abs / base).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const frac = exp > 0 ? (abs % base).toString().padStart(exp, "0") : "";
  const showFrac = exp > 0 && !(opts.trimZeroCents && /^0+$/.test(frac));
  const num = showFrac ? whole + "." + frac : whole;
  const sym = opts.symbol === false ? "" : (SYMBOL[currency] ?? currency + " ");
  return (neg ? "-" : "") + sym + num;
}

export function addMoney(...amounts: Minor[]): Minor {
  let total = 0;
  for (const a of amounts) { assertMinor(a); total += a; }
  assertMinor(total, "sum");
  return total;
}

export function subtractMoney(a: Minor, b: Minor): Minor {
  assertMinor(a); assertMinor(b);
  return a - b;
}

export function multiplyMoney(unit: Minor, qty: number): Minor {
  assertMinor(unit, "unit");
  if (!Number.isSafeInteger(qty)) throw new TypeError("qty must be an integer");
  const r = unit * qty;
  assertMinor(r, "product");
  return r;
}

/** Integer division rounding half away from zero. */
export function divRoundHalfUp(numerator: number, denominator: number): number {
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator) || denominator === 0) {
    throw new TypeError("divRoundHalfUp requires safe integers and non-zero denominator");
  }
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator), d = Math.abs(denominator);
  const q = Math.floor(n / d), r = n - q * d;
  return sign * (r * 2 >= d ? q + 1 : q);
}

/** Percentage of an amount expressed in basis points (1000 bp = 10%), rounded half-up. */
export function percentOf(minor: Minor, basisPoints: number): Minor {
  assertMinor(minor);
  if (!Number.isSafeInteger(basisPoints)) throw new TypeError("basisPoints must be an integer");
  const p = minor * basisPoints;
  if (!Number.isSafeInteger(p)) throw new RangeError("Amount too large for percentage calculation");
  return divRoundHalfUp(p, 10_000);
}

/** Apply a percentage discount; returns { discount, net }. */
export function applyPercentDiscount(minor: Minor, basisPoints: number): { discount: Minor; net: Minor } {
  const discount = percentOf(minor, basisPoints);
  return { discount, net: minor - discount };
}

/**
 * Split an amount by integer ratios so that parts always sum to the original
 * (largest-remainder method; ties go to earlier parts).
 */
export function allocateMoney(minor: Minor, ratios: number[]): Minor[] {
  assertMinor(minor);
  if (ratios.length === 0) throw new Error("ratios must not be empty");
  if (ratios.some((r) => !Number.isSafeInteger(r) || r < 0)) throw new TypeError("ratios must be non-negative integers");
  const total = ratios.reduce((a, b) => a + b, 0);
  if (total === 0) throw new Error("ratios must not all be zero");
  const sign = minor < 0 ? -1 : 1;
  const abs = Math.abs(minor);
  const parts = ratios.map((r) => Math.floor((abs * r) / total));
  const rema = ratios.map((r, i) => ({ i, rem: (abs * r) % total }));
  let left = abs - parts.reduce((a, b) => a + b, 0);
  rema.sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (let k = 0; left > 0; k++, left--) parts[rema[k % rema.length].i] += 1;
  return parts.map((p) => p * sign);
}
